import { HStack, IconButton, Text } from '@chakra-ui/react'
import { FileDoc, Image as ImageIcon, MusicNote, Play, X } from '@phosphor-icons/react'
import type { Accept } from 'react-dropzone'
import api from '../../services/api'
import type { UploadedFile } from '../../hooks/useFileUpload'

// Mesma lista de extensões que o POST /upload/file aceita (UPLOAD_EXTNAMES na API)
export const TICKET_DROPZONE_ACCEPT: Accept = {
  'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  'video/*': ['.mp4', '.webm', '.mov', '.avi', '.mkv'],
  'audio/*': ['.mp3', '.wav', '.ogg', '.oga', '.m4a', '.opus', '.aac', '.amr', '.3gp', '.flac'],
  'application/*': ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.zip', '.rar'],
  'text/plain': ['.txt'],
}

export const TICKET_FILE_INPUT_ACCEPT =
  'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.txt'

export const MAX_TICKET_ATTACHMENTS = 10

/** Arquivos colados (Ctrl+V de print, por exemplo). */
export function filesFromClipboard(e: React.ClipboardEvent): File[] {
  const items = e.clipboardData?.items
  if (!items) return []
  const files: File[] = []
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (item.kind === 'file') {
      const file = item.getAsFile()
      if (file) files.push(file)
    }
  }
  return files
}

/** Payload que a API de tickets espera em `attachments`. */
export function toAttachmentPayload(uploaded: UploadedFile) {
  return {
    fileUrl: uploaded.url,
    fileType: uploaded.fileType,
    fileName: uploaded.fileName,
    fileSize: uploaded.fileSize,
  }
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return 'Arquivo'
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Baixa pelo proxy da API (o link direto do Spaces abre no navegador em vez de baixar). */
export async function downloadTicketAttachment(messageId: number, fileName: string | null) {
  const res = await api.get(`/internal-tickets/messages/${messageId}/download`, { responseType: 'blob' })
  const blobUrl = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = fileName || 'arquivo'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(blobUrl)
}

export function PendingFileChips({
  files,
  onRemove,
  bg,
  isDisabled,
}: {
  files: File[]
  onRemove: (index: number) => void
  bg: string
  isDisabled?: boolean
}) {
  return (
    <>
      {files.map((file, index) => (
        <HStack key={`${file.name}-${index}`} bg={bg} px={3} py={2} borderRadius="lg" spacing={2} maxW="100%">
          {file.type.startsWith('image/') ? (
            <ImageIcon size={16} />
          ) : file.type.startsWith('video/') ? (
            <Play size={16} />
          ) : file.type.startsWith('audio/') ? (
            <MusicNote size={16} />
          ) : (
            <FileDoc size={16} />
          )}
          <Text fontSize="sm" noOfLines={1} maxW="140px">
            {file.name}
          </Text>
          <Text fontSize="xs" color="gray.500" flexShrink={0}>
            {formatFileSize(file.size)}
          </Text>
          <IconButton
            aria-label="Remover"
            icon={<X size={14} />}
            size="xs"
            variant="ghost"
            isDisabled={isDisabled}
            onClick={() => onRemove(index)}
          />
        </HStack>
      ))}
    </>
  )
}
